'use client';

import {
  cloneElement,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ElementType,
  type ReactNode,
} from 'react';
import { animate, motion, useInView, useReducedMotion } from 'framer-motion';
import { PortableText, type PortableTextComponents } from '@portabletext/react';
import type { PortableTextBlock } from 'sanity';
import { stegaClean } from '@sanity/client/stega';

import { cn } from '@/lib/utils';
import Eyebrow from '../Eyebrow';

export const EXPO = [0.16, 1, 0.3, 1] as const;

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const WS = /^[ \t\r\n]+$/;

function word(text: string, key: string) {
  return (
    <span key={key} className="rz-rv-m">
      <span className="rz-rv-u">{text}</span>
    </span>
  );
}

function splitWords(node: ReactNode, path = 'w'): ReactNode[] {
  if (typeof node === 'string') {
    return node
      .split(/([ \t\r\n]+)/)
      .filter(Boolean)
      .map((part, i) => (WS.test(part) ? part : word(part, `${path}.${i}`)));
  }
  if (Array.isArray(node)) return node.flatMap((child, i) => splitWords(child, `${path}.${i}`));
  if (isValidElement<{ children?: ReactNode }>(node) && node.props.children != null) {
    const inner = splitWords(node.props.children, path);
    const lead = typeof inner[0] === 'string' && WS.test(inner[0]) ? (inner.shift() as string) : '';
    const last = inner[inner.length - 1];
    const trail = typeof last === 'string' && WS.test(last) ? (inner.pop() as string) : '';
    return [lead, cloneElement(node, undefined, inner), trail].filter((part) => part !== '');
  }
  return [node];
}

function splitComponents(base: PortableTextComponents): PortableTextComponents {
  const blocks = (typeof base.block === 'object' ? base.block : {}) as Record<string, ComponentType<{ children?: ReactNode }>>;
  const wrapped = Object.fromEntries(
    Object.entries(blocks).map(([style, Block]) => [
      style,
      (props: { children?: ReactNode }) => <Block {...props}>{splitWords(props.children)}</Block>,
    ]),
  );
  return { ...base, block: wrapped as PortableTextComponents['block'] };
}

type Mode = 'title' | 'text';

function useWordReveal(mode: Mode, delay: number) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.35 });
  const [armed, setArmed] = useState(false);
  const [shown, setShown] = useState(false);

  useIsoLayoutEffect(() => {
    if (!reduce) setArmed(true);
  }, [reduce]);

  useIsoLayoutEffect(() => {
    const root = ref.current;
    if (!root || !inView || !armed) return;
    const units = Array.from(root.querySelectorAll<HTMLElement>('.rz-rv-u'));
    if (mode === 'title') {
      let line = -1;
      let top = -Infinity;
      units.forEach((unit) => {
        const box = unit.parentElement!.getBoundingClientRect();
        const threshold = 0.5 * parseFloat(getComputedStyle(unit).fontSize);
        if (box.top - top > threshold) {
          line += 1;
          top = box.top;
        }
        unit.style.transitionDelay = `${delay + line * 0.12}s`;
      });
    } else {
      units.forEach((unit, i) => {
        unit.style.transitionDelay = `${delay + i * 0.012}s`;
      });
    }
    setShown(true);
  }, [inView, armed, mode, delay]);

  return {
    ref,
    className: cn(mode === 'title' ? 'rz-rv rz-rv-title' : 'rz-rv rz-rv-text', armed && 'is-armed', shown && 'is-in'),
  };
}

export function RevealRichText({
  value,
  components,
  mode = 'title',
  delay = mode === 'title' ? 0 : 0.25,
  className,
}: {
  value?: PortableTextBlock[];
  components: PortableTextComponents;
  mode?: Mode;
  delay?: number;
  className?: string;
}) {
  const split = useMemo(() => splitComponents(components), [components]);
  const reveal = useWordReveal(mode, delay);
  if (!value?.length) return null;
  return (
    <div ref={reveal.ref} className={cn(className, reveal.className)}>
      <PortableText value={value} components={split} />
    </div>
  );
}

export function RevealWords({
  text,
  as: Tag = 'p',
  mode = 'text',
  delay = mode === 'title' ? 0 : 0.25,
  className,
}: {
  text?: string;
  as?: ElementType;
  mode?: Mode;
  delay?: number;
  className?: string;
}) {
  const reveal = useWordReveal(mode, delay);
  if (!text) return null;
  return (
    <Tag ref={reveal.ref} className={cn(className, reveal.className)}>
      {splitWords(text)}
    </Tag>
  );
}

export function RevealEyebrow({ text, className }: { text?: string; className?: string }) {
  const reduce = useReducedMotion();
  if (!text) return null;
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ opacity: { duration: 0.6, ease: 'easeInOut' }, y: { duration: 0.9, ease: EXPO } }}
      className={className}>
      <Eyebrow text={text} />
    </motion.div>
  );
}

export function RevealBlock({
  index = 0,
  className,
  children,
  as = 'div',
}: {
  index?: number;
  className?: string;
  children: ReactNode;
  as?: 'div' | 'li';
}) {
  const reduce = useReducedMotion();
  const Tag = as === 'li' ? motion.li : motion.div;
  return (
    <Tag
      initial={reduce ? false : { opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12, margin: '0px 0px -6% 0px' }}
      transition={{
        opacity: { duration: 0.9, ease: EXPO, delay: Math.min(index * 0.08, 0.48) },
        y: { duration: 1.1, ease: EXPO, delay: Math.min(index * 0.08, 0.48) },
      }}
      className={className}>
      {children}
    </Tag>
  );
}

type Numeric = { prefix: string; suffix: string; amount: number; sep: string; decimals: number };

export function parseNumeric(raw?: string): Numeric | null {
  const clean = stegaClean(raw) ?? '';
  const match = clean.match(/^(\D*?)(\d[\d.,]*)(\D*)$/);
  if (!match) return null;
  const [, prefix, digits, suffix] = match;
  const grouped = digits.match(/^\d{1,3}(?:([.,])\d{3})+$/);
  if (grouped) {
    const amount = Number(digits.split(grouped[1]).join(''));
    return Number.isFinite(amount) ? { prefix, suffix, amount, sep: grouped[1], decimals: 0 } : null;
  }
  const amount = Number(digits.replace(/,/g, ''));
  if (!Number.isFinite(amount)) return null;
  const dot = digits.lastIndexOf('.');
  return { prefix, suffix, amount, sep: '', decimals: dot >= 0 ? digits.length - dot - 1 : 0 };
}

export function formatNumeric(spec: Numeric, n: number) {
  const [int, frac] = Math.abs(n).toFixed(spec.decimals).split('.');
  const grouped = spec.sep ? int.replace(/\B(?=(\d{3})+(?!\d))/g, spec.sep) : int;
  return `${spec.prefix}${grouped}${frac ? `.${frac}` : ''}${spec.suffix}`;
}

export function CountUp({ value, className }: { value?: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduce = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.35 });
  const clean = stegaClean(value) ?? '';
  const spec = useMemo(() => (/^\d/.test(clean) ? parseNumeric(clean) : null), [clean]);
  const [display, setDisplay] = useState(clean);

  useIsoLayoutEffect(() => {
    if (spec && !reduce) setDisplay(formatNumeric(spec, 0));
  }, [spec, reduce]);

  useEffect(() => {
    if (!spec || reduce) {
      setDisplay(clean);
      return;
    }
    if (!inView) return;
    const controls = animate(0, spec.amount, {
      duration: 1.5,
      delay: 0.25,
      ease: (p) => 1 - Math.pow(1 - p, 4),
      onUpdate: (latest) => setDisplay(formatNumeric(spec, latest)),
      onComplete: () => setDisplay(clean),
    });
    return () => controls.stop();
  }, [inView, spec, reduce, clean]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}

export function RevealStyles() {
  return (
    <style
      jsx
      global>{`
      .rz-rv-m,
      .rz-rv-u {
        display: inline-block;
        font-family: inherit;
      }
      .rz-rv-text .rz-rv-m {
        clip-path: inset(-0.25em -0.3em 0);
        padding-bottom: 0.12em;
        margin-bottom: -0.12em;
      }
      .rz-rv-title .rz-rv-m,
      .rz-rv-title .rz-rv-u {
        line-height: 0;
      }
      .rz-rv-title .rz-rv-m {
        clip-path: inset(-0.9em -0.3em -0.66em);
      }
      .rz-rv-u {
        transition: transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
      }
      .rz-rv.is-armed:not(.is-in) .rz-rv-u {
        transition: none;
      }
      .rz-rv-text.is-armed:not(.is-in) .rz-rv-u {
        transform: translateY(115%);
      }
      .rz-rv-title.is-armed:not(.is-in) .rz-rv-u {
        transform: translateY(1.4em);
      }
      .rz-rv :is(.hl-wash, .hl-texture, .hl-tick-l) {
        clip-path: inset(-20% -20% -20% 0);
        transition: clip-path 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.12s;
      }
      .rz-rv .hl-tick-r {
        transition: opacity 0.25s ease 0.45s;
      }
      .rz-rv.is-armed:not(.is-in) :is(.hl-wash, .hl-texture, .hl-tick-l) {
        clip-path: inset(-20% 100% -20% 0);
        transition: none;
      }
      .rz-rv.is-armed:not(.is-in) .hl-tick-r {
        opacity: 0;
        transition: none;
      }
      @media (prefers-reduced-motion: reduce) {
        .rz-rv .rz-rv-u {
          transform: none !important;
          transition: none !important;
        }
        .rz-rv :is(.hl-wash, .hl-texture, .hl-tick-l, .hl-tick-r) {
          clip-path: none !important;
          opacity: 1 !important;
          transition: none !important;
        }
      }
    `}</style>
  );
}
